import {computed, DestroyRef, effect, inject, Injectable, signal, untracked} from '@angular/core'
import {BetslipRiskInfoDto} from '@/api/player-api/models/betslip-risk-info-dto'
import {CreateBetslipDto} from '@/api/player-api/models/create-betslip-dto'
import {BetslipCreateService} from '@/features/betslip/betslip-create/betslip-create.service'
import {BetslipConverterService} from '@/features/betslip/betslip-create/lib/converters/betslip-converter.service'
import {BetslipWorkbenchStore} from './betslip-workbench.store'
import {forVipPlayer, mockBetslipRiskInfo, RiskInfoStatus} from './mock-betslip-risk-backend'
import {LiabilityLimitsState, PlayerLiabilityTier} from './liability-limits.state'

/** The backend call this service makes — the mock today, `BetslipWsApi.betslipRiskInfo` once it exists. */
export type RiskInfoSource = (payload: CreateBetslipDto) => Promise<BetslipRiskInfoDto>

export interface RiskExchange {
	status: RiskInfoStatus
	at: Date
	request?: CreateBetslipDto
	response?: BetslipRiskInfoDto
	error?: string
}

/** The reply when the workbench's limits are not applied: no ceilings, no problems. */
const NO_CEILINGS: BetslipRiskInfoDto = {
	problemCode: 0,
	problems: [],
	riskLimitDto: {upperLimit: 0, vipUpperLimit: 0, upperPayLimit: 0},
	selections: [],
	systems: []
}

/** Long enough that ticking through System Options sends one request, not one per tick. */
const RISK_DEBOUNCE_MS = 250

/**
 * The workbench's `BetslipRiskService`: asks for risk info when its inputs change and merges the
 * reply into the isolated betslip through `mergeRemoteProblems`, the entry point production uses.
 *
 * Refetch triggers are Selections added or removed, any Selection's odds, the Betslip Type,
 * a System Option toggled, plus any edit on the Liability tab, which is the mock backend's
 * configuration, and a change of the player the workbench picks. Never on a stake change.
 *
 * Status: only `ready` asks the backend. `pending` and `loggedOut` leave the Betslip without
 * risk info, as production does before the reply arrives or when nobody is logged in; `failed`
 * makes the request fail.
 */
@Injectable()
export class WorkbenchRiskService {

	private readonly store = inject(BetslipWorkbenchStore)
	private readonly betslip = inject(BetslipCreateService)
	private readonly converter = inject(BetslipConverterService)

	private readonly player = inject(LiabilityLimitsState).playerTier

	/**
	 * Priced for the player the workbench picks: as is for a normal player, `forVipPlayer` for a VIP,
	 * and with no ceilings at all when the workbench's limits are not applied.
	 */
	private readonly source: RiskInfoSource = (payload) => {
		const tier = this.player()
		if (tier === PlayerLiabilityTier.off) {
			return Promise.resolve(NO_CEILINGS)
		}

		const reply = mockBetslipRiskInfo(this.store.liabilityLimits(), payload)
		return Promise.resolve(tier === PlayerLiabilityTier.vip ? forVipPlayer(reply) : reply)
	}

	readonly last = signal<RiskExchange | undefined>(undefined)

	/** Everything the risk request depends on, and nothing that changes with the stake. */
	private readonly inputs = computed(() => {
		const data = this.store.data()

		return JSON.stringify({
			betType: data.betType,
			selections: data.selections.map((s) => [s.oddsId, s.odds, !!s.isBanker]),
			systems: data.systems.filter((s) => s.checked).map((s) => s.count)
		})
	})

	private sequence = 0
	private timer: ReturnType<typeof setTimeout> | undefined

	constructor() {
		effect(() => {
			this.inputs()
			this.store.liabilityLimits()
			this.store.riskStatus()
			this.player()

			untracked(() => this.schedule())
		})

		inject(DestroyRef).onDestroy(() => clearTimeout(this.timer))
	}

	private schedule(): void {
		clearTimeout(this.timer)
		this.timer = setTimeout(() => void this.fetch(), RISK_DEBOUNCE_MS)
	}

	private async fetch(): Promise<void> {
		const data = this.store.data()
		const status = this.store.riskStatus()
		const sequence = ++this.sequence

		if (data.selections.length === 0) {
			this.last.set(undefined)
			return
		}

		const request = this.converter.createPayload(data)

		if (status === RiskInfoStatus.pending || status === RiskInfoStatus.loggedOut) {
			this.last.set({status, at: new Date(), request})
			return
		}

		try {
			if (status === RiskInfoStatus.failed) {
				throw new Error('Mock backend: request failed (Status is set to "Request failed")')
			}

			const response = await this.source(request)
			if (sequence !== this.sequence) {
				return
			}

			this.last.set({status, at: new Date(), request, response})
			this.betslip.mergeRemoteProblems(structuredClone(response))
		} catch (error) {
			if (sequence === this.sequence) {
				this.last.set({status, at: new Date(), request, error: error instanceof Error ? error.message : String(error)})
			}
		}
	}
}
